<template>
  <div v-if="tour" class="tour-detail">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap" rel="stylesheet">
    <div class="tour-header" :style="{ backgroundImage: `url(${tour.image})` }">
      <div class="tour-header-content">
        <div class="content-wrapper">
          <h1>{{ tour.title }}</h1>
          <p>{{ tour.subtitle }}</p>
        </div>
      </div>
    </div>
    
    <div class="tour-content">
      <div class="tour-overview">
        <div class="city-info-container">
          <div class="city-info">
            <h3>About the Tour</h3>
            <p>{{ tour.description }}</p>
          </div>
          <div class="tour-map">
            <div class="map-container">
              <img :src="tourMapImage" alt="Tour route map">
            </div>
          </div>
        </div>
        
        <div class="tour-summary">
          <div class="summary-item">
            <div class="summary-icon">
              <font-awesome-icon icon="calendar" />
            </div>
            <h3>{{ tour.days.length }} Days</h3>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon">
              <font-awesome-icon icon="map-marker-alt" />
            </div>
            <h3>{{ tour.destinations || '10' }} Destinations</h3>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon">
              <font-awesome-icon icon="mountain-sun" />
            </div>
            <h3>{{ tour.attractions || '10' }} Attractions</h3>
          </div>
        </div>
      </div>
      
      <div class="day-by-day">
        <h2>Day by Day Tour</h2>
        <div class="day-cards">
          <div v-for="(day, index) in tour.days" :key="index" class="day-card">
            <div class="day-header">
              <div class="day-number">Day {{ day.day }}</div>
              <div class="day-title">{{ day.title }}</div>
            </div>
            
            <div class="day-content">
              <div class="day-text">
                <div class="day-description">
                  <ul>
                    <li v-for="(item, i) in formatDayDescription(day.description)" :key="i" v-html="item">
                    </li>
                  </ul>
                </div>
              </div>
              
              <div class="day-image">
                <img :src="day.image || defaultDayImage(index)" :alt="`Day ${day.day} journey: ${day.title}`">
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- New FAQ Section -->
      <div class="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div class="faq-list" v-if="faqItems && faqItems.length > 0">
          <div v-for="(faq, index) in faqItems" :key="faq.id" class="faq-item" :class="{ 'active': activeFaq === index }">
            <div class="faq-question" @click="toggleFaq(index)">
              <h3>{{ faq.question }}</h3>
              <div class="toggle-icon">
                <font-awesome-icon :icon="activeFaq === index ? 'minus' : 'plus'" />
              </div>
            </div>
            <div class="faq-answer" :class="{ 'open': activeFaq === index }">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <div v-else class="loading">
    <p>Loading tour information...</p>
  </div>
  
  <TestimonialsSection class="title-red-background" />
  <TravelerMemories :altBackground="false" />
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { tourData, tourCategories } from '../data/tourData';
import { faqForTour } from '../data/faqForTour';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TestimonialsSection from '../components/TestimonialsSection.vue';
import TravelerMemories from '../components/TravelerMemories.vue';

const route = useRoute();
const router = useRouter();
const activeFaq = ref(-1);

const categoryParam = computed(() => route.params.category);

const categoryDisplayName = computed(() => {
  return tourCategories[categoryParam.value]?.name || categoryParam.value;
});

const tour = computed(() => {
  if (!tourData[categoryParam.value]) return null;
  return tourData[categoryParam.value].find(t => t.id === route.params.id) || null;
});

// Get the appropriate FAQ items for the current tour category
const faqItems = computed(() => {
  return faqForTour[categoryParam.value] || [];
});

// Toggle FAQ item
const toggleFaq = (index) => {
  if (activeFaq.value === index) {
    activeFaq.value = -1;
  } else {
    activeFaq.value = index;
  }
};

// Route map image based on the tour
const tourMapImage = computed(() => {
  if (!tour.value) return '/images/tours/maps/default-map.jpg';
  return tour.value.tourMapImage || '/images/tours/maps/default-map.jpg';
});

// Default day images if none provided
const defaultDayImage = (index) => {
  const defaultImages = [
    '/images/scenes/scene1.jpg',
    '/images/scenes/scene2.jpg',
    '/images/scenes/scene3.jpg',
    '/images/scenes/scene4.jpg',
    '/images/scenes/scene5.jpg'
  ];
  
  return defaultImages[index % defaultImages.length];
};

// 格式化每日描述为列表项
const formatDayDescription = (description) => {
  if (!description) return [];
  
  // 方法1：按句号或分号拆分文本成列表项
  const items = description.split(/\.\s+|\;\s+/)
    .filter(item => item.trim().length > 0)
    .map(item => {
      // 确保每个列表项末尾有适当的标点
      let formattedItem = item.trim();
      if (!formattedItem.endsWith('.') && !formattedItem.endsWith('!') && !formattedItem.endsWith('?')) {
        formattedItem += '.';
      }
      // 确保每个列表项首字母大写
      formattedItem = formattedItem.charAt(0).toUpperCase() + formattedItem.slice(1);
      
      // 加粗关键词：地点名称、景点、活动和文化元素
      const keywordsToHighlight = [
        // Classic Tour
        `Dukezong Ancient Town`, `Zhongyi Market's`, `Shuhe Old Town`, `Lugu Lake`, `Liwubi Island`, `Luoshui Village`, `Mosuo bonfire`,
        `Zhamei Temple`, `Nise Village`, `Xiao Luoshui Village`, `Mosuo family home`, `Mosuo Museum`, `Yangtze River's famous First Bend scenic area`,
        `Xingyi`, `Wanfenglin Scenic Area`, `Huangguoshu Waterfall`, `Tianlong Tunbao Ancient Town`, `Jiaxiu Pavilion`, `Qingyan Ancient Town`,
        `Xizhou Ancient Town`, `Three-Course Tea ceremony`, `Zhoucheng village`, `boat cruise`, `Weishan Ancient Town`, `Donglianhua Village`,
        `Tiger Mouth terraces`, `local markets`, `Zhu Family Garden`, `soft trekking in Yuanyang rice terrace`, `Pu’er`, 
        `learn about the unique production methods of Pu’er tea`, `Wild Elephant Valley`, `Jinghong's Night Market`, 
        `Xishuangbanna Tropical Botanical Garden`, `nearby ethnic villages`, `Nan Nuo Mountain`, `Dai village`, `Cizhong`, `Cizhong Catholic Church`,
        `Tacheng Black Snub-Nosed Monkey Park`, `First Bend on the Yangtze River`, `Trekking route`, `Qiaotou Town`,


        // Tea Tour
        `Kunming`, `Green Lake Park`, `Kunming Old Street`, `Yuantong Temple`, `Naigu Stone Forest`, `guided soft trekking tours`,
        `China Pu'er Tea Exhibition Garden`, `Pu'er Sun River National Forest Park`, `Tropic of Cancer Marker Park`, 
        `the village of Naka Li`, `Horse Caravan Culture Museum`, `Tea Horse City Night Market`, `Ancient Tea Forests`,
        `Ethnic Minority Villages`, `Manggen village`, `Golden Pagoda Temple`, `Nuogan Dai Village`, `Nuogan Dai Village`, 
        `Wengji Bulang Village`, `Dapingzhang Forest`, `Mangjing Upper Village`, `Mangjing Lower Village`, `Manghong Octagonal Tower`,
        `Dapingzhang Ancient Tea Forest`, `Dali Old Town`, `boat cruise on Erhai Lake`, `Xizhou Town`, `three-course tea ceremony`,
        `Zhoucheng`, `Dayan Old Town`, `Black Dragon Pond`, `Yuhu Village`, `Baisha Village`, `Shuhe old town`, `Dongba Culture Museum`,
        `the First Bend of the Yangtze River`, `Shangri-La`, `Buddhist Temples`, `Royal Relics`, `Nayun Ancient Town`, 
        `Shangcheng and Zhongcheng Buddhist Temples`, `Menglian Tusi Palace`, `Menglian Grand Golden Pagoda`, `Pu'er`,

        // Trekking Tour
        `Naxi Family Guesthouse`, `Tea Horse Guesthouse`, `Halfway Guesthouse`, `Tina's Guesthouse`, `"Sky Crack" (Yixiantian)`, `Tiger Leaping Gorge`,
        `Naxi Family Guesthouse`, `scenic "28 Bends"`, `Tea Horse Guesthouse`, `Tina's Guesthouse`, `Sky Ladder`, `168 Steps Ladder`,
        `Great Jingsha River Corner`, `Dongzhulin Monastery`, `Meili Snow mountain`, `Feilaisi Temple`, `Mingyong Glacier`, `Yubeng Village`,
        `Sacred Waterfall`, `waterfall`, `Ice Lake`, `Ninong Canyon`, `Songzanlin Monastery`, `Lijiang Old Town`, `Great Jingsha River Corner`,
        `Visit a local Tibetan family`, `Xilianpen Pass`, `Abuji Lake`, `Laoyaoshan trails`, `Ganheba Ridge Trail`, `Spruce Meadow`, `Blue Moon Valley`,

        // Short Day Tour
        `Naigu Stone Forest`, `West Mountain's Dragon Gate`, `guided soft trekking tours`, 'Jianshui', `Jianshui Ancient Town`,
        `Confucius Temple`, `Yuanyang`, `Laohuzui (Tiger Mouth)`, `Duoyishu`, `Qingkou Hani Village`, `Xizhou Town`, `three-course tea ceremony`,
        `Zhoucheng`, `Shibaoshan Grottoes`, `Shaxi old town`, `Dayan Old Town`, `Black Dragon Pond`, `Yuhu Village`,
        `Jade Dragon Snow Mountain`, `Yak Meadow`, `Impression Lijiang show`, `Baisha Ancient Town`, `Baisha Murals`,
        `Shaxi Ancient Town`, `Shibao Mountain`, `Shizhongshan Grottoes`, `Songzanlin Monastery`, `Pudacuo National Park`, 

        // City
        `Kunming`, `Lijiang`, `Dali`, `Yuanyang`, `Shangri-La`, `Pu’er`, `Pu'er`, `Jianshui`, `Deqin`
      ];
      
      keywordsToHighlight.forEach(keyword => {
        // 匹配关键词（不区分大小写）
        // Escape special regex characters in keywords
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Use word boundary for single words, but not for phrases with spaces
        const pattern = keyword.includes(' ') 
          ? escapedKeyword
          : `\\b${escapedKeyword}\\b`;
        
        const regex = new RegExp(pattern, 'gi');
        formattedItem = formattedItem.replace(regex, match => `<strong>${match}</strong>`);
      });
      
      return formattedItem;
    });
  
  return items;
};

onMounted(() => {
  if (!tour.value) {
    console.error(`Tour not found for category ${categoryParam.value} and id ${route.params.id}`);
    router.push('/tours');
  }
});
</script>

<style scoped>
.tour-detail {
  min-height: 100vh;
  background-color: var(--color-background-soft);
}

.tour-header {
  min-height: 450px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  color: white;
}

.tour-header-content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
}

.content-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  background-color: rgb(0 0 0 / 27%);
  border-radius: 8px;
  padding: 1.5rem;
}

.tour-header h1 {
  font-size: 4rem;
  font-family: serif;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  display: inline-block;
  padding: 0;
  margin: 0.5rem 0;
}

.tour-header p {
  font-size: 1.5rem;
  max-width: 600px;
  display: inline-block;
  padding: 0;
  margin: 0;
}

.tour-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.tour-overview h2, 
.day-by-day h2 {
  font-size: 2.2rem;
  font-family: serif;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #333;
  position: relative;
}

.tour-overview h2::after, 
.day-by-day h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 50px;
  height: 3px;
  background-color: #008c5c;
}

.city-info-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin: 2rem 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  overflow: hidden;
}

.city-info, .tour-map {
  padding: 2rem;
}

.city-info h3 {
  font-size: 2rem;
  font-family: Serif;
  color: #333;
  margin-bottom: 1rem;
}

.city-info p {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #44464a;
}

.map-container {
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  position: relative;
  background-color: transparent;
  border: none;
  box-shadow: none;
}

.map-container img {
  object-fit: contain;
  width: 100%;
  height: 100%;
  object-position: center;
  position: static;
  display: block;
  margin: 0 auto;
  box-shadow: none;
  border: none;
}

.tour-summary {
  display: flex;
  justify-content: space-around;
  margin: 1rem 0;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.summary-item {
  text-align: center;
  flex: 1;
  min-width: 100px;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
}

.summary-icon {
  background: rgba(0,140,92,0.1);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon .svg-inline--fa {
  font-size: 1.4rem;
  color: #008c5c;
}

.summary-item h3 {
  font-size: 1.2rem;
  color: #333;
  margin: 0;
}

.day-cards {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.day-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.day-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--color-border);
  flex-wrap: wrap;
}

.day-number {
  display: inline-block;
  background: #008c5c;
  color: white;
  padding: 0.1rem 1rem;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1.5rem;
  margin-bottom: 0;
  width: fit-content;
  flex-shrink: 0;
}

.day-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 0;
  flex-grow: 1;
}

.location-path {
  line-height: 1.4;
}

.day-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 1.5rem;
}

.day-text {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.day-description {
  font-size: 1.1rem;
  font-family: 'Noto Sans', sans-serif;
  line-height: 1.6;
  color: #44464a;
}

.day-image {
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
}

.day-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 1.2rem;
  color: #666;
}

.day-description ul {
  padding-left: 1.5rem;
  margin: 0;
}

.day-description li {
  margin-bottom: 0.8rem;
  line-height: 1.6;
  font-size: 1.1rem;
  color: #44464a;
}

.day-description li:last-child {
  margin-bottom: 0;
}

@media (max-width: 1024px) {
  .city-info-container {
    grid-template-columns: 1fr;
  }
  
  .day-content {
    grid-template-columns: 1fr;
  }
  
  .day-image {
    height: 250px;
    order: -1;
  }
  
  .map-container {
    height: 350px;
  }
}

@media (max-width: 768px) {
  .tour-header {
    min-height: 300px;
  }
  
  .tour-header h1 {
    font-size: 2.5rem;
    margin: 0.4rem 0;
  }
  
  .tour-header p {
    font-size: 1.2rem;
  }

  .content-wrapper {
    padding: 1.2rem;
  }
  
  .tour-content {
    padding: 2rem 1.5rem;
  }
  
  .tour-overview h2,
  .day-by-day h2 {
    font-size: 2rem;
  }
  
  .city-info, .tour-map {
    padding: 1.5rem;
  }
  
  .map-container {
    height: 300px;
  }
  
  .day-header {
    padding: 1rem;
  }
  
  .day-number {
    font-size: 1.3rem;
  }
  
  .day-title {
    font-size: 1.1rem;
  }
  
  .day-location {
    font-size: 0.9rem;
  }
  
  .day-content {
    padding: 1rem;
    gap: 1.5rem;
  }
  
  .day-description li {
    font-size: 1rem;
    margin-bottom: 0.6rem;
  }
  
  .day-image {
    height: 220px;
  }
  
  .tour-summary {
    display: none;
  }
  
  .summary-item {
    padding: 1.2rem;
    min-width: 90px;
    gap: 0.6rem;
  }
  
  .summary-icon {
    width: 45px;
    height: 45px;
  }
  
  .summary-icon .svg-inline--fa {
    font-size: 1.2rem;
  }
  
  .summary-item h3 {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .tour-header {
    min-height: 250px;
  }
  
  .tour-header h1 {
    font-size: 2rem;
    margin: 0.3rem 0;
  }
  
  .tour-header p {
    font-size: 1.1rem;
  }
  
  .content-wrapper {
    padding: 1rem;
  }
  
  .tour-content {
    padding: 2rem 1rem;
  }
  
  .tour-overview h2,
  .day-by-day h2 {
    font-size: 1.8rem;
  }
  
  .city-info p {
    font-size: 1rem;
  }
  
  .map-container {
    height: 250px;
  }
  
  .tour-summary {
    display: none;
  }
  
  .summary-item {
    padding: 1rem;
    min-width: 80px;
    gap: 0.5rem;
  }
  
  .summary-icon {
    width: 40px;
    height: 40px;
  }
  
  .summary-icon .svg-inline--fa {
    font-size: 1.1rem;
  }
  
  .summary-item h3 {
    font-size: 1rem;
  }
  
  .day-header {
    padding: 0.8rem;
    gap: 0.8rem;
  }
  
  .day-number {
    font-size: 1.2rem;
    padding: 0.2rem 0.8rem;
  }
  
  .day-title {
    font-size: 1rem;
  }
  
  .day-location {
    font-size: 0.85rem;
    gap: 0.5rem;
    margin-top: 0.3rem;
  }
  
  .day-description li {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
  
  .day-image {
    height: 180px;
  }
}

/* FAQ Section Styles */
.faq-section {
  margin-top: 4rem;
  background: white;
  border-radius: 8px;
  padding: 2.5rem;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.faq-section h2 {
  font-size: 2.2rem;
  font-family: serif;
  font-weight: bold;
  margin-bottom: 2rem;
  color: #333;
  position: relative;
}

.faq-section h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 50px;
  height: 3px;
  background-color: #008c5c;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.faq-item {
  border: 1px solid #eaeaea;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.faq-item.active {
  border-color: #008c5c;
  box-shadow: 0 5px 15px rgba(0,140,92,0.1);
}

.faq-question {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: #f9f9f9;
  transition: background-color 0.3s ease;
}

.faq-item.active .faq-question {
  background-color: rgba(0,140,92,0.1);
}

.faq-question h3 {
  font-size: 1.1rem;
  margin: 0;
  color: #333;
  font-weight: 600;
  transition: color 0.3s ease;
}

.faq-item.active .faq-question h3 {
  color: #008c5c;
}

.toggle-icon {
  color: #888;
  transition: transform 0.3s ease, color 0.3s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: white;
}

.faq-item.active .toggle-icon {
  transform: rotate(180deg);
  color: #008c5c;
  background-color: rgba(0,140,92,0.1);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s ease, padding 0.3s ease;
  padding: 0 1.5rem;
}

.faq-answer.open {
  max-height: 1000px;
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.faq-answer p {
  margin: 0;
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
}

@media (max-width: 1024px) {
  .faq-section {
    padding: 2rem;
  }
  
  .faq-section h2 {
    font-size: 2rem;
  }
  
  .faq-question h3 {
    font-size: 1rem;
  }
}

@media (max-width: 768px) {
  .faq-section {
    padding: 1.5rem;
    margin-top: 3rem;
  }
  
  .faq-section h2 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
  
  .faq-question {
    padding: 1.2rem;
  }
  
  .faq-answer.open {
    padding: 0 1.2rem 1.2rem 1.2rem;
  }
}

@media (max-width: 480px) {
  .faq-section {
    padding: 1.2rem;
    margin-top: 2.5rem;
  }
  
  .faq-question h3 {
    font-size: 0.95rem;
  }
  
  .faq-answer p {
    font-size: 0.9rem;
  }
}
</style>

<!-- 非 scoped 样式，可以影响 v-html 插入的内容 -->
<style>
.day-description li strong {
  font-weight: 600;
  color: black; /* 使用网站主题色 */
  padding: 0 1px;
}
</style> 